import { useMemo, useCallback } from "react";
import { Wrench, Layers, GripVertical, Sparkles } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ComponentPicker } from "./ComponentPicker";
import { ModelImporter } from "./ModelImporter";
import { ActiveComponentCard } from "./ActiveComponentCard";
import { SortableComponentCard } from "./SortableComponentCard";
import { CustomTemplateWarning, useCustomTemplateValidation } from "./CustomTemplateWarning";
import type { Configuration, ConfigComponent } from "./types";

interface CustomConfigurationBuilderProps {
  configuration: Configuration;
  onConfigurationChange: (config: Configuration) => void;
  customerLastName?: string;
}

export function CustomConfigurationBuilder({
  configuration,
  onConfigurationChange,
  customerLastName
}: CustomConfigurationBuilderProps) {
  // Separate active (quantity > 0) and available (quantity = 0) components
  const { activeComponents, availableComponents } = useMemo(() => ({
    activeComponents: configuration.components.filter(c => c.quantity > 0),
    availableComponents: configuration.components.filter(c => c.quantity === 0)
  }), [configuration.components]);

  // Validation warnings
  const warnings = useCustomTemplateValidation(configuration.components);

  // Add component: set quantity to 1
  const handleAddComponent = (componentId: string) => {
    onConfigurationChange({
      ...configuration,
      components: configuration.components.map(c =>
        c.id === componentId 
          ? { ...c, quantity: Math.min(1, c.maxQuantity) }
          : c
      )
    });
  };

  // Remove component: set quantity to 0
  const handleRemoveComponent = (componentId: string) => {
    onConfigurationChange({
      ...configuration,
      components: configuration.components.map(c =>
        c.id === componentId 
          ? { ...c, quantity: 0 }
          : c
      )
    });
  };

  // Update quantity (clamped to min 1, max maxQuantity for active components)
  const handleQuantityChange = (componentId: string, quantity: number) => {
    const component = configuration.components.find(c => c.id === componentId);
    if (!component) return;

    const clampedQuantity = Math.max(1, Math.min(quantity, component.maxQuantity));
    
    onConfigurationChange({
      ...configuration,
      components: configuration.components.map(c =>
        c.id === componentId 
          ? { ...c, quantity: clampedQuantity }
          : c
      )
    });
  };

  // Update material
  const handleMaterialChange = (componentId: string, materialId: string) => {
    onConfigurationChange({
      ...configuration,
      components: configuration.components.map(c =>
        c.id === componentId 
          ? { ...c, materialId }
          : c
      )
    });
  };

  // Update dimension
  const handleDimensionChange = (
    componentId: string, 
    dimension: 'width' | 'height' | 'thickness', 
    value: number | null
  ) => {
    onConfigurationChange({
      ...configuration,
      components: configuration.components.map(c =>
        c.id === componentId 
          ? { ...c, [dimension]: value }
          : c
      )
    });
  };

  // Update attribute
  const handleAttributeChange = (
    componentId: string,
    attributeId: string,
    value: string | number | null
  ) => {
    onConfigurationChange({
      ...configuration,
      components: configuration.components.map(c => {
        if (c.id === componentId) {
          return {
            ...c,
            attributes: c.attributes.map(attr =>
              attr.id === attributeId ? { ...attr, value } : attr
            )
          };
        }
        return c;
      })
    });
  };

  // Handle custom name change
  const handleCustomNameChange = (value: string) => {
    onConfigurationChange({
      ...configuration,
      customName: value
    });
  };

  // Smart default: suggest "Hautakivi [Sukunimi]"
  const handleSuggestName = () => {
    if (customerLastName) {
      onConfigurationChange({
        ...configuration,
        customName: `Hautakivi ${customerLastName}`
      });
    }
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end - reorder components
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = activeComponents.findIndex(c => c.id === active.id);
      const newIndex = activeComponents.findIndex(c => c.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedActive = arrayMove(activeComponents, oldIndex, newIndex);
        
        // Rebuild components array: reordered active + unchanged available
        const newComponents = [
          ...reorderedActive,
          ...availableComponents
        ];

        onConfigurationChange({
          ...configuration,
          components: newComponents
        });
      }
    }
  }, [activeComponents, availableComponents, configuration, onConfigurationChange]);

  return (
    <div className="space-y-6">
      {/* Header with custom name input */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
        <Wrench className="h-5 w-5 text-amber-600 mt-1" />
        <div className="flex-1 space-y-3">
          {/* Nimikenttä */}
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-sm text-amber-700 dark:text-amber-300 mb-1 block">
                Tuotteen nimi (valinnainen)
              </Label>
              <Input
                value={configuration.customName || ""}
                onChange={(e) => handleCustomNameChange(e.target.value)}
                placeholder={
                  customerLastName 
                    ? `esim. Hautakivi ${customerLastName}` 
                    : "esim. Hautakivi Mallikas, Erikoistyö..."
                }
                className="bg-white dark:bg-background border-amber-300 dark:border-amber-700 focus:border-amber-400"
              />
            </div>
            {/* Smart Default -nappi */}
            {customerLastName && !configuration.customName && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSuggestName}
                className="shrink-0 border-amber-300 hover:bg-amber-100 dark:border-amber-700 dark:hover:bg-amber-900/30"
              >
                <Sparkles className="h-4 w-4 mr-1" />
                Ehdota
              </Button>
            )}
          </div>
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Valitse vapaasti mitä komponentteja tilaus sisältää
          </p>
        </div>
      </div>

      {/* Validation warnings */}
      {warnings.length > 0 && (
        <CustomTemplateWarning warnings={warnings} />
      )}

      {/* Active components with drag and drop */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-muted-foreground" />
          <Label className="text-base font-semibold">Valitut komponentit</Label>
          <Badge variant="secondary">{activeComponents.length} kpl</Badge>
          {activeComponents.length > 1 && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <GripVertical className="h-3 w-3" />
              Vedä järjestääksesi
            </span>
          )}
        </div>

        {activeComponents.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={activeComponents.map(c => c.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-3">
                {activeComponents.map(component => (
                  <SortableComponentCard
                    key={component.id}
                    component={component}
                    onRemove={() => handleRemoveComponent(component.id)}
                    onQuantityChange={(qty) => handleQuantityChange(component.id, qty)}
                    onMaterialChange={(matId) => handleMaterialChange(component.id, matId)}
                    onDimensionChange={(dim, val) => handleDimensionChange(component.id, dim, val)}
                    onAttributeChange={(attrId, val) => handleAttributeChange(component.id, attrId, val)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
            <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Ei vielä valittuja komponentteja</p>
            <p className="text-sm">Lisää komponentteja alta</p>
          </div>
        )}
      </div>

      {/* Import from product model */}
      <ModelImporter
        onImport={(importedComponents) => {
          onConfigurationChange({
            ...configuration,
            components: [...configuration.components, ...importedComponents],
          });
        }}
      />

      {/* Component picker */}
      <ComponentPicker
        availableComponents={availableComponents}
        onAddComponent={handleAddComponent}
      />
    </div>
  );
}
